import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/system/Box';
import { OrganizationsTable } from './OrganizationsTable';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Checkbox } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import { useTranslation } from 'react-i18next';
import { CurrentUserContext } from '../../contexts/CurrentUserContext';
import { IBasicOrganization } from '../../resources/models/IOrganization';
import Button from '@mui/material/Button';
import { apiUser } from '../../resources/api/apiUser';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { mapAdminRoles } from '../../helpers/mapRoles';

interface IProps {
  cancelText: string;
  cancelAction: () => void;
  submitText: string;
  submitAction: (organization: IBasicOrganization, setDefault: boolean) => void;
  noOrgText: string;
  noOrgAction: () => void;
}

const { useUpdateDefaultOrganization } = apiUser;

export const ChooseOrganization: FC<IProps> = ({
  cancelText,
  cancelAction,
  submitText,
  submitAction,
  noOrgText,
  noOrgAction,
}) => {
  const { t } = useTranslation();
  const { userInfo } = useContext(CurrentUserContext);

  const { organization } = useContext(OrganizationContext);

  const [selected, setSelected] = useState<IBasicOrganization | null>(organization || null);
  const [saveDefault, setSaveDefault] = useState(false);

  const { Guard: GuardSetDefault } = useUpdateDefaultOrganization(userInfo?.id);

  const onSubmit = useCallback(async () => {
    if (!userInfo) return null;
    const organization = userInfo?.organizations?.find((organization) => organization.id === selected?.id);
    if (!organization) return null;

    submitAction(organization, saveDefault && userInfo?.default_organization !== organization.id);
  }, [selected, submitAction, userInfo?.organizations, userInfo?.default_organization, saveDefault]);

  const forceSetDefault = userInfo?.default_organization === null || userInfo?.default_organization === selected?.id;

  const mappedAdminRoles = useMemo(() => (userInfo ? mapAdminRoles(userInfo.roles) : []), [userInfo?.roles]);

  useEffect(() => {
    if (forceSetDefault) {
      setSaveDefault(true);
    } else {
      setSaveDefault(false);
    }
  }, [forceSetDefault]);

  return (
    <>
      <DialogTitle>{t('chooseOrganization.title')}</DialogTitle>
      <DialogContent>
        <Box mb={2} mt={2}>
          <OrganizationsTable
            value={selected}
            onChange={setSelected}
            defaultOrganizationId={userInfo?.default_organization}
          />
        </Box>
        <GuardSetDefault>
          <FormControlLabel
            control={
              <Checkbox
                checked={saveDefault}
                onChange={() => setSaveDefault(!saveDefault)}
                disabled={!selected || forceSetDefault}
              />
            }
            label={t('chooseOrganization.useAsDefault')}
          />
        </GuardSetDefault>
      </DialogContent>
      <DialogActions>
        <Button key="cancel" onClick={cancelAction}>
          {cancelText}
        </Button>
        {/*
         * Show no-organization option only to Pixotope Accounts and to users with no organizations
         */}
        {mappedAdminRoles.length > 0 || (userInfo?.organizations || []).length === 0 ? (
          <Button key="noorg" variant="outlined" onClick={noOrgAction}>
            {noOrgText}
          </Button>
        ) : null}
        <Button key="submit" variant="contained" disabled={!selected} onClick={onSubmit}>
          {submitText}
        </Button>
      </DialogActions>
    </>
  );
};
